<template>
  <div>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <div>
        <b-row>
          <b-col md="6">
            <FormulateInput
              id="name"
              v-model="filter.name"
              :label="$t('Nome da linha')"
              :placeholder="$t('Nome')"
            />
          </b-col>
        </b-row>
      </div>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>
      <b-table
        ref="lines-table"
        show-empty
        responsive
        striped
        class="bordered"
        :fields="fields"
        :empty-text="getEmptyTableMessage($tc('PRODUCT_LINE.NAME'), 'female', queryExecuted)"
        :items="productLines"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(active)="row">
          <e-status-badge :status="row.item.active" />
        </template>

        <template #cell(action)="row">
          <e-grid-actions
            :is-active="row.item.active"
            :show-update="$can('Update', 'ProductLine')"
            :show-delete="$can('Delete', 'ProductLine')"
            :show-activate="$can('Activate', 'ProductLine')"
            :show-deactivate="$can('Deactivate', 'ProductLine')"
            @update="showUpdateLineSidebar(row)"
            @delete="deleteLine(row)"
            @activate="activateDeactivateLine(row)"
            @deactivate="activateDeactivateLine(row)"
          />
        </template>
      </b-table>
      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="lines-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <product-line-sidebar
      ref="line_sidebar"
      @save="getData"
    />

    <fab
      v-if="$can('Create', 'ProductLine')"
      :main-tooltip="$t('Adicionar Linha')"
      @click="showCreateBrandSidebar"
    />
  </div>
</template>

<script>
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import {
  EPageSizeSelector,
  EPaginationSummary,
  EStatusBadge,
  EFilters,
  EGridActions,
} from '@/views/components'
import fab from '@/views/components/FAB.vue'
import { mapActions, mapState } from 'vuex'
import ProductLineSidebar from './ProductLineSidebar.vue'

export default {
  name: 'ProductLine',
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    fab,
    EStatusBadge,
    EFilters,
    ProductLineSidebar,
    EGridActions,
  },

  mixins: [],

  data() {
    return {
      queryExecuted: false,
      showSidebar: false,
      fetching: false,
      edit: false,
      saving: false,
    }
  },

  computed: {
    ...mapState('pages/catalog/productLines', {
      productLines: 'productLines',
      paging: 'paging',
      sorting: 'sorting',
      filter: 'filter',
    }),

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Id'),
          key: 'id',
          class: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
        },
        {
          label: this.$t('Nome'),
          key: 'name',
          trClass: 'text-center',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('Status'),
          key: 'active',
          class: 'text-center',
          thStyle: { width: '90px' },
          sortable: true,
        },
      ]
    },
  },

  mounted() {},

  methods: {
    ...mapActions('pages/catalog/products', { comboFetchDomainData: 'fetchDomainData' }),
    ...mapActions('pages/catalog/productLines', [
      'fetchProductLines',
      'resetFilters',
      'setCurrentPage',
      'setPageSize',
    ]),

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchProductLines()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.filterData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    showCreateBrandSidebar() {
      this.$refs.line_sidebar.show()
    },

    async showUpdateLineSidebar(row) {
      this.$refs.line_sidebar.show({ id: row.item.id })
    },

    async deleteLine(line) {
      const { id } = line.item
      try {
        const confirm = await this.confirm()
        if (confirm) {
          await this.$http.delete(`/api/lines/${id}`)
          this.comboFetchDomainData('lines')
          this.getData()
          this.showSuccess({ message: this.$t('Linha removida com sucesso') })
        }
      } catch (error) {
        this.showError({ error })
      }
    },

    async activateDeactivateLine(row) {
      try {
        const confirm = await this.confirm()
        if (confirm) {
          const path = row.item.active ? 'deactivate' : 'activate'
          await this.$http({
            url: `/api/lines/${path}/${row.item.id}`,
            method: 'PUT',
          })
          this.comboFetchDomainData('lines')
          this.getData()
          this.showSuccess({
            message: this.$t(`${row.item.active === true ? 'Inativado' : 'Ativado'} com sucesso`),
          })
        }
      } catch (error) {
        this.showError({ error })
      }
    },
  },
}
</script>

<style></style>
